/**
 * The capture form.
 *
 * This is the product's front door: everything else is a view over what this screen saves. Its shape
 * comes from the Phase 0 prototype, which was built and then used, and whose finding was that the
 * original form charged curation cost at capture time — eight fields of which one was required, and
 * nothing said which.
 *
 * Two tiers, and **no field was removed**. The question of *when* to ask for something changed:
 *
 * - **Tier 1** is one input and one action. It takes either a pasted link or a typed name, because
 *   most saves start with a link and asking for the link and then the name doubles the work for the
 *   common case. A name field appears only when the input was a link — the one situation where a name
 *   is still needed and the link cannot supply one.
 * - **Tier 2** is everything else behind a disclosure, equally available and not in the way.
 *
 * Three things are deliberately not here, and each absence is a decision:
 *
 * **No provider autocomplete.** Measured: a prefix query does not work against Nominatim — `Thác Da`
 * returns a hit and `Thác Datan` returns nothing — so a provider-driven suggestion list would go
 * *empty* as the user typed more of the word. Suggestions while typing come from the user's own places,
 * which is instant, offline, and usually what they want. The provider sits behind an explicit button.
 *
 * **No screenshot upload.** ADR-004 made MVP capture user-authored, and the API's `sources[].url` is a
 * stored reference the server never fetches. A screenshot would need storage, EXIF stripping and a
 * purge job — Phase 2 item 2.8.
 *
 * **No coordinate requirement.** A place saved without one is a complete asset (AC-2.5). It will not
 * enter a route, and the form says so rather than refusing the save.
 */

import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { AiPlaceLookupResult, BestTimeSlot, DuplicateCandidate } from "@trippool/contracts";
import type { CreatePlaceBody } from "@trippool/api-client";
/*
 * A value import as well as a type: the enum's `options` is what validates the category that arrived in
 * the query string. `z.enum` is both a type and a runtime value, and importing only the type would leave
 * this file trusting a string from the URL to be a member of the vocabulary.
 */
import { PlaceCategory } from "@trippool/contracts";

import { categoryChoices, copy } from "../copy.ts";
import { useGoBack } from "../lib/useGoBack.ts";
import { useToast } from "../components/Toast.tsx";
import {
  asUrl,
  capturePrefill,
  foldForCompare,
  matchConfidence,
  nameFromUrl,
  nearestSavedAreas,
  planForLocationInput,
  toggleBestTime,
} from "../lib/capture.ts";
import { fieldMessagesFor, messageFor } from "../lib/errors.ts";
import { getStoredGeminiKey } from "../lib/geminiKey.ts";
import {
  useAiPlaceLookup,
  useAreas,
  useCreateArea,
  useCreatePlace,
  useDuplicateCheck,
  useMergePlaces,
  useOwnPlaceSuggestions,
  usePlace,
  usePlaceLookup,
  usePlaces,
  useUpdatePlace,
} from "../lib/queries.ts";
import { searchCentre } from "../lib/placeSearch.ts";
import { useDebouncedValue } from "../lib/useDebouncedValue.ts";
import { useCurrentUser, useSessionManager } from "../lib/session-context.tsx";

/**
 * The four suitability answers, in the order the detail screen lists them.
 *
 * The labels are the `placeDetail` ones, deliberately: the form and the screen that shows the result
 * name the same four things, and a second set of words here is how "Trẻ em" in the form and "Phù hợp
 * với trẻ em" on the card become two features that are one.
 */
const SUITABILITY_CHOICES: Array<{
  value: "is_indoor" | "kids_friendly" | "elderly_friendly" | "pet_friendly";
  label: string;
}> = [
  { value: "is_indoor", label: copy.placeDetail.indoor },
  { value: "kids_friendly", label: copy.placeDetail.kids },
  { value: "elderly_friendly", label: copy.placeDetail.elderly },
  { value: "pet_friendly", label: copy.placeDetail.pets },
];

const BEST_TIME_CHOICES: Array<{ value: BestTimeSlot; label: string }> = [
  { value: "sáng", label: copy.capture.bestTimeMorning },
  { value: "trưa", label: copy.capture.bestTimeNoon },
  { value: "chiều-tối", label: copy.capture.bestTimeEvening },
  { value: "cả ngày", label: copy.capture.bestTimeAllDay },
];

/*
 * The map is loaded on demand, and only when the disclosure is open.
 *
 * Leaflet is ~150 kB and it is the single largest thing this route could pull in. A user adding a
 * place from a pasted link never opens this section, and making them download a map renderer for a
 * form they will not scroll past is the kind of cost that shows up in a bundle budget and nowhere
 * else. `lazy` plus `Suspense` keeps it out of the initial chunk.
 */
const MapPicker = lazy(async () => {
  const module = await import("../components/MapPicker.tsx");
  return { default: module.MapPicker };
});

/**
 * How long the find field waits before asking the provider.
 *
 * The upstream allows 5 requests/second and one search costs an autocomplete plus a detail call per
 * candidate, so a request per keystroke would spend the ceiling on a word the user has not finished
 * typing. Long enough for a pause between words, short enough that the list feels like it is keeping up.
 */
const FIND_DEBOUNCE_MS = 350;

/** A provider result the user can adopt. */
interface LookupCandidate {
  name: string;
  address: string | null;
  /**
   * The provider's own categories, filtered to the contract's vocabulary by the handler.
   *
   * Carried so adopting a result fills the category chips: the provider already answered "what kind of
   * place is this", and asking the user to answer it again is the typing this form exists to remove.
   */
  categories: PlaceCategory[];
  /**
   * The coordinate, and never a placeholder.
   *
   * The candidate list is built from results that *have* a position: the contract allows
   * `location: null`, and the first version of this screen substituted `{lat: 0, lon: 0}` for it — a
   * place in the Gulf of Guinea that would have been saved as if a provider had put it there. A result
   * without a coordinate is dropped instead.
   */
  location: { lat: number; lon: number };
  /** Which index answered, stored as `place_sources.provider` when the user adopts this. */
  provider: string;
  providerPlaceId: string | null;
  matchQuality: number;
}

/**
 * A field the capture form can fill from a source rather than from the user.
 *
 * Module scope because it is a property of the form's shape, not of one render: the mark beside a field
 * and the state that decides it must agree on the set, and a string literal in two places is how they
 * stop agreeing.
 */
type DerivedField = "name" | "address" | "categories" | "area" | "location";

function formatSourceSummary(url: string): { platform: string; label: string } {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host.includes("tiktok.com")) {
      const match = parsed.pathname.match(/(@[a-zA-Z0-9_.-]+)/);
      return {
        platform: copy.capture.sourcePlatforms.tiktok,
        /*
         * `match?.[1] ??` and not `match ? match[1] :`.
         *
         * With `noUncheckedIndexedAccess`, any index access is `T | undefined`, so `match[1]` is not a
         * `string` even when `match` is non-null — the checker cannot know the pattern's first group is
         * mandatory. The other branches below survive that only by accident: a template literal accepts
         * `undefined` and would have printed the word, while this one assigns to a `string` and is a
         * compile error. The nullish form is the same behaviour and needs no lie about the type.
         */
        label: match?.[1] ?? (parsed.pathname.length > 25 ? `${parsed.pathname.slice(0, 25)}…` : parsed.pathname),
      };
    }
    if (host.includes("instagram.com")) {
      const match = parsed.pathname.match(/\/(p|reel|tv)\/([a-zA-Z0-9_-]+)/);
      return { platform: copy.capture.sourcePlatforms.instagram, label: match ? `${match[1]}/${match[2]}` : parsed.pathname };
    }
    if (host.includes("google.") || host.includes("maps.app.goo.gl") || host.includes("goo.gl")) {
      return {
        platform: copy.capture.sourcePlatforms.googleMaps,
        label: parsed.pathname.length > 25
          ? `${parsed.pathname.slice(0, 25)}…`
          : (parsed.pathname || copy.capture.sourceLocationFallback),
      };
    }
    if (host.includes("facebook.com") || host.includes("fb.watch")) {
      return { platform: copy.capture.sourcePlatforms.facebook, label: parsed.pathname.length > 25 ? `${parsed.pathname.slice(0, 25)}…` : parsed.pathname };
    }
    const cleanHost = host.replace(/^www\./, "");
    const cleanPath = parsed.pathname === "/" ? "" : parsed.pathname;
    return { platform: cleanHost, label: cleanPath.length > 25 ? `${cleanPath.slice(0, 25)}…` : cleanPath };
  } catch {
    return { platform: copy.capture.sourcePlatforms.link, label: url.length > 30 ? `${url.slice(0, 30)}…` : url };
  }
}

/**
 * The mark beside a field this form filled from a source rather than from the user.
 *
 * A small badge rather than a sentence, because it must sit in a label row without pushing it around,
 * and it says only the one thing the user needs: this value was not typed by you, so check it.
 */
function DerivedMark(): ReactElement {
  return <span className="badge badge--derived">{copy.capture.derivedMark}</span>;
}

/**
 * What the save-time duplicate step found, in three states rather than two.
 *
 * `matches` is candidates the database's rule can see. `unchecked` is the third state the screen used to
 * drop on the floor: the rule needs a coordinate as well as a name, so a draft without one cannot be
 * checked at all — and returning in silence made "could not run" byte-for-byte identical to "ran and
 * found nothing". Not-known is not a match, so it is named.
 */
type DuplicateNotice =
  | { kind: "matches"; candidates: DuplicateCandidate[] }
  | { kind: "unchecked" };

export function CaptureScreen(): ReactElement {
  const navigate = useNavigate();
  const manager = useSessionManager();
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const { showToast } = useToast();

  /*
   * One query parameter, and it exists so a different screen can hand this form a starting point:
   * `/capture?place=<id>` from the manage list to edit.
   *
   * It is not required. A save started from the tab is still a save, and appears in the manage list.
   */
  const [params] = useSearchParams();
  const editingId = params.get("place") ?? undefined;
  /*
   * Where this form's work belongs once it is finished or abandoned.
   *
   * Cancel goes back, and when there is nothing behind this page it goes to the place being edited — this
   * form's own parent — rather than to the list of every place the user has. A successful save goes to the
   * same place, deliberately: an edit's outcome is a change to *that* place, and the card is what shows it,
   * while a new place's outcome is a row in the list. The two-answer version that shipped before this —
   * a success panel offering "Xem danh sách" / "Thêm địa điểm" — was the owner's complaint: it confirmed
   * the save on a screen that had nothing on it, leaving the user one more tap from the thing they saved.
   */
  const originPath = editingId === undefined ? "/places" : `/places/${editingId}`;
  const goBack = useGoBack(originPath);
  const isEditing = editingId !== undefined;

  /*
   * Everything another screen can hand this form, read once.
   *
   * The reported defect this fixes: the Home screen's hero field navigated to `/capture?q=<what was
   * pasted>` and nothing here read `q`, so the form opened **empty** — the user pasted a link, pressed
   * Lưu, arrived at a blank form, and pasted the same link again. Measured in the browser: the URL
   * carried the link while `.field__input--lead` held `""`. The link's provenance was lost with it,
   * because `sources` is built from the leading field and the leading field was empty.
   *
   * The parsing itself is `capturePrefill` in `lib/capture.ts`: a pure function of the query string, so
   * the rule "a parameter the screen is given is a parameter it uses" has a test that fails when the
   * screen stops using it.
   */
  const prefill = useMemo(() => capturePrefill(params), [params]);
  const fromMap = prefill.name !== "";

  const areas = useAreas(userId);
  const createArea = useCreateArea(userId);
  const createPlace = useCreatePlace(userId);
  const aiLookup = useAiPlaceLookup();
  const [aiCandidates, setAiCandidates] = useState<AiPlaceLookupResult[] | null>(null);
  const aiAutoSearched = useRef(false);
  const lastAiQueryRef = useRef<string>("");

  /*
   * One idempotency key per place this form is saving, rotated when the form starts another.
   *
   * This is the client half of the duplicate-save fix. The API collapses two requests that carry the
   * same `Idempotency-Key`, but the client generated a fresh one per call, so two submissions of this
   * one form — a double tap, or the retry of a request whose response was lost — were two keys and the
   * server had nothing to collapse them with. Holding one key for the whole form is what makes them one
   * save.
   *
   * The rotation is the mount: a save navigates away (to the list, or to the edited place's card), which
   * unmounts this form, and the next place is authored on a fresh mount with a fresh key. A form that
   * stayed put would have to rotate its own key before writing a second place — the "add another" button
   * that used to do it is gone with the success panel, and no path saves two places from one mount now.
   */
  const saveKey = useRef<string>(crypto.randomUUID());
  /*
   * The in-flight guard, and a ref rather than `isPending`.
   *
   * `createPlace.isPending` only becomes true after React re-renders, which is after both handlers of a
   * fast double tap have already run. A ref is set synchronously, so the second tap returns before it
   * reaches the mutation — measured in the browser, two clicks in one task sent two POSTs.
   */
  const submitting = useRef(false);

  // ---- tier 1
  /*
   * The leading field starts from what another screen handed over, which is the whole point of the
   * query parameters read above. `text` wins over `name` because `text` is what the user typed or
   * pasted and `name` is a screen's interpretation of it; when only a name arrived, that is the entry.
   */
  const [primary, setPrimary] = useState(() => (prefill.text !== "" ? prefill.text : prefill.name));
  const [name, setName] = useState(prefill.name);
  // Opened when a provider candidate arrived: the address, categories and coordinate are the reason
  // that screen sent the user here, and leaving them behind a collapsed disclosure hides the work.
  const [detailsOpen, setDetailsOpen] = useState(fromMap);

  // ---- tier 2
  const [address, setAddress] = useState(prefill.address);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [categories, setCategories] = useState<PlaceCategory[]>(
    prefill.category === null ? [] : [prefill.category],
  );
  const [subcategory, setSubcategory] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<Array<{ id: string; name: string }>>([]);
  const [areaDraft, setAreaDraft] = useState("");
  const [bestTime, setBestTime] = useState<BestTimeSlot[]>([]);
  /*
   * The four suitability answers, each a three-state boolean.
   *
   * `null` is "not recorded" and is not the same as `false`: the detail screen renders the three
   * differently, and a recorded "no" is information the user chose to record (AC-2.5). All four were
   * stored and displayed and **unsettable from any screen** — the form had no control for them, so the
   * only way to a value was a raw API call. Hence two chips per row rather than a checkbox: a checkbox
   * cannot say "no", only "yes" or "unanswered".
   */
  const [suitability, setSuitability] = useState<{
    is_indoor: boolean | null;
    kids_friendly: boolean | null;
    elderly_friendly: boolean | null;
    pet_friendly: boolean | null;
  }>({ is_indoor: null, kids_friendly: null, elderly_friendly: null, pet_friendly: null });
  const setSuitabilityFlag = (key: keyof typeof suitability, value: boolean): void => {
    // Tapping the chosen answer again clears it, which is the only way back to "not recorded".
    setSuitability((current) => ({ ...current, [key]: current[key] === value ? null : value }));
  };
  const [note, setNote] = useState("");
  const [location, setLocation] = useState<{ lat: number; lon: number } | null>(prefill.location);
  // The viewport moves to a coordinate that arrived from another screen, so the user sees the pin they
  // picked on the map rather than a default view of Đà Lạt with a marker somewhere off screen.
  const [focus, setFocus] = useState<{ lat: number; lon: number } | null>(prefill.location);

  // ---- the find field
  const [find, setFind] = useState("");
  const [looking, setLooking] = useState(false);
  const [candidates, setCandidates] = useState<LookupCandidate[] | null>(null);
  const [lookupStatus, setLookupStatus] = useState<"ok" | "unavailable" | "quota_exceeded" | null>(null);
  /**
   * The text the user pressed Tìm for, when the field's own contents are not it.
   *
   * The suggestions and the button go through one query, so there is one request path and one cache
   * entry — the alternative was two sources writing the same list, and whichever wrote last winning.
   * `null` means "search what is in the field, once the user pauses"; a value means "search this now",
   * which is what the button pins so a search runs without waiting for the debounce.
   */
  const [submittedFind, setSubmittedFind] = useState<string | null>(null);
  /**
   * The provider whose candidate was adopted, so the save records where the coordinate came from.
   *
   * `place_sources.provider` with `kind = 'provider_search'` is what lets the same place be recognised
   * again by identity rather than by name. Without it, adopting a Goong result saved a coordinate with
   * no provenance at all, which is the one thing that makes the search worth having.
   */
  const [adoptedProvider, setAdoptedProvider] = useState<{ provider: string; providerPlaceId: string | null } | null>(
    prefill.provider !== null ? { provider: prefill.provider, providerPlaceId: prefill.providerPlaceId } : null,
  );
  const mapCardRef = useRef<HTMLDivElement>(null);

  /**
   * The expanded URL behind a Google Maps share link, when the server resolved one.
   *
   * Separate from `primary` because the field keeps what the user pasted while this holds what it points
   * at. Only the source list reads it.
   */
  const [resolvedSourceUrl, setResolvedSourceUrl] = useState<string | null>(null);
  const [formError, setFormError] = useState<unknown>(null);

  /*
   * Which fields this form filled from a source rather than from the user.
   *
   * A set rather than a flag per field, because a field can be derived, edited (which clears it), then
   * derived again — and on open several may already be derived from the query string a screen handed
   * over. The mark is what keeps a derived value distinguishable from a typed one: "tự điền" beside the
   * label, cleared by the field's own change handler, so the badge always describes the value that is
   * actually in the field.
   */
  const [derivedFields, setDerivedFields] = useState<ReadonlySet<DerivedField>>(() => {
    const initial = new Set<DerivedField>();
    if (prefill.name !== "") initial.add("name");
    if (prefill.address !== "") initial.add("address");
    if (prefill.category !== null) initial.add("categories");
    if (prefill.location !== null) initial.add("location");
    return initial;
  });
  const markDerivedField = (...fields: DerivedField[]): void => {
    setDerivedFields((current) => new Set([...current, ...fields]));
  };
  const clearDerivedField = (field: DerivedField): void => {
    setDerivedFields((current) => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };

  /*
   * The live name and pin, for the one adoption path that returns after an await.
   *
   * `adoptCoordinate` has to decide whether it filled the name, and whether the pin was empty, from the
   * values that are true at the moment it writes — not from the values captured when the resolve
   * started. That is the same reasoning its own `setState` updaters use, in a form a plain read can see.
   * Updated in an effect, so it trails a render rather than leading it: a name typed, or a pin placed,
   * during the round trip is in here before the response is applied.
   */
  const formSnapshotRef = useRef<{ name: string; primary: string; location: { lat: number; lon: number } | null }>({
    name: prefill.name,
    primary: prefill.text,
    location: prefill.location,
  });
  useEffect(() => {
    formSnapshotRef.current = { name, primary, location };
  }, [name, primary, location]);

  /*
   * The save-time duplicate step (AC-3.5), as the three-state notice above.
   *
   * `null` is "not asked yet"; `matches` is candidates the database's rule can see; `unchecked` is the
   * coordinate-less draft it cannot run on. Still a warning, not a block: `duplicateAcknowledged` is what
   * lets "Vẫn lưu" fall through to the create on the re-run. Measured, the rule flags two branches of one
   * chain 149 m apart — a false alarm it cannot avoid — so the user must be able to say "these are two
   * places" without friction.
   */
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateNotice | null>(null);
  const duplicateAcknowledged = useRef(false);
  const duplicateCheck = useDuplicateCheck();
  const mergePlaces = useMergePlaces(userId);
  /*
   * The caller's own places, read only to infer a nearby area once a coordinate exists.
   *
   * Gated on `location !== null` so a form opened without a pin does not pay for a list it will not
   * read, and on `!isEditing` because an existing place's areas are already loaded and must not be
   * overwritten by a derivation.
   */
  const savedPlaces = usePlaces(userId, { limit: 100 }, location !== null && !isEditing);

  const existing = usePlace(userId, editingId ?? "");
  const updatePlace = useUpdatePlace(userId, editingId ?? "");
  /*
   * Prefilled exactly once.
   *
   * Reported defect F3 in the UX plan was the opposite of this: editing a saved place appeared not to
   * load its data, because the form re-initialised on every render and overwrote what the user typed.
   * A ref makes the load happen once, so a later keystroke is never discarded.
   */
  const prefilled = useRef(false);
  useEffect(() => {
    const place = existing.data;
    if (place === undefined || prefilled.current) return;
    prefilled.current = true;
    setName(place.name);
    setPrimary(place.name);
    if (place.address !== null) setAddress(place.address);
    setCategories(place.categories);
    if (place.subcategory !== null) setSubcategory(place.subcategory);
    setBestTime(place.best_time);
    if (place.description !== null) setNote(place.description);
    setLocation(place.location);
    setSelectedAreas(place.areas);
    setSuitability({
      is_indoor: place.is_indoor,
      kids_friendly: place.kids_friendly,
      elderly_friendly: place.elderly_friendly,
      pet_friendly: place.pet_friendly,
    });
    setDetailsOpen(true);
  }, [existing.data]);

  const primaryAsUrl = asUrl(primary);
  const sourceSummary = primaryAsUrl !== null ? formatSourceSummary(primaryAsUrl) : null;
  const suggestions = useOwnPlaceSuggestions(userId, primary, primaryAsUrl === null && primary.trim().length >= 2);

  /** The name that would be saved, from whichever input is authoritative. */
  const effectiveName = primaryAsUrl === null ? primary.trim() : name.trim();

  /*
   * The provider search behind the find field.
   *
   * A name typed into the field is asked of the provider once the user pauses — two characters in, which
   * is the point at which a prefix is worth asking about — and the answer appears under the field. The
   * centre is the pin the user already placed, or the viewport, or the app's reference destination; it is
   * a ranking hint sent to the provider and never a coordinate that gets saved.
   *
   * A pasted link is not searched for by name: it is classified locally (a full Maps URL, a coordinate
   * pair) or resolved through `/places/map-link`, which is what `applyLocationPlan` does before this
   * query is ever consulted.
   */
  const trimmedFind = find.trim();
  const findAsName = asUrl(trimmedFind) === null;
  const centre = searchCentre(location, focus);
  const findText = submittedFind ?? trimmedFind;
  const debouncedFind = useDebouncedValue(findText, submittedFind === null ? FIND_DEBOUNCE_MS : 0);
  const placeLookup = usePlaceLookup(userId, debouncedFind, centre, findAsName);

  /*
   * One list, from the query, so a suggestion and a button press cannot disagree.
   *
   * A result with no coordinate is dropped rather than given a placeholder: it cannot be adopted, and
   * `{lat: 0, lon: 0}` is a position the provider never stated.
   */
  useEffect(() => {
    if (placeLookup.isError) {
      setCandidates([]);
      setLookupStatus("unavailable");
      const q = debouncedFind.trim();
      if (q.length >= 3 && asUrl(q) === null && lastAiQueryRef.current !== q && !aiLookup.isPending) {
        lastAiQueryRef.current = q;
        void runAiLookup(q);
      }
      return;
    }
    const data = placeLookup.data;
    if (data === undefined) return;
    setLookupStatus(data.status);
    const newCandidates = data.results.flatMap((result) =>
      result.location === null
        ? []
        : [
            {
              name: result.name,
              address: result.address,
              categories: result.categories,
              location: result.location,
              provider: result.provider,
              providerPlaceId: result.provider_place_id,
              matchQuality: result.match_quality,
            },
          ],
    );
    setCandidates(newCandidates);

    if (newCandidates.length > 0) {
      setAiCandidates(null);
    } else {
      const q = debouncedFind.trim();
      if (q.length >= 3 && asUrl(q) === null && lastAiQueryRef.current !== q && !aiLookup.isPending) {
        lastAiQueryRef.current = q;
        void runAiLookup(q);
      }
    }
    // `runAiLookup` and `aiLookup.isPending` are read on purpose without being dependencies:
    // `runAiLookup` is redefined every render, so listing it would make this effect re-run on every
    // render and re-ask the provider in a loop. The `lastAiQueryRef` guard above is what makes the
    // reads safe, and the sibling effect that auto-runs the AI search carries the same disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeLookup.data, placeLookup.isError, debouncedFind]);

  /*
   * One "busy" flag for the button.
   *
   * Two things can be in flight: the provider search, and the server-side resolution of a pasted share
   * link. They are different operations with the same visual answer, and a button that lit up for only
   * one of them would look unresponsive for the other.
   */
  const searching = looking || placeLookup.isFetching;

  /*
   * Adopt the location in the leading field, without asking.
   *
   * The flow this serves is the common one: the user copies a place's link, pastes it on Home, presses
   * Lưu. Before this they got a form with the link in it and an empty pin, and had to find the coordinate
   * themselves — measured, the geocoder cannot find two of ten real Đà Lạt venues, so for those there was
   * no path at all short of dragging the map.
   *
   * Five guards, each for a real case:
   * - **Once per input.** Without the ref this fires on every render the field changes on, which for a
   *   short link means a Google request per keystroke.
   * - **Never over an existing pin.** A coordinate the user placed by hand is theirs; adopting over it
   *   would move a deliberate choice.
   * - **Not while editing.** An existing place's `name` fills this field, and a place may legitimately be
   *   named something that looks like a URL. Adopting from it would rewrite a saved pin on open.
   * - **Only something that is a location.** `planForLocationInput` classifies, and `search` is excluded
   *   so the geocoder stays behind its button — a name typed here is not a request.
   * - **Not a bare pair the user is midway through typing.** `11.94` is not a pair and `11.94, 108` is
   *   not one the parser accepts, so the ref is keyed on the exact text and the classification gates it.
   *
   * **The guard used to be `primaryAsUrl !== null`, and that was a hole.** A coordinate pair is the other
   * shape this feature promises, and it parses to a coordinate while being no URL at all — so pasting
   * `11.9213822, 108.4428672` adopted nothing, and saving produced a place named `11.9213822, 108.4428672`
   * with no coordinate, which the planner then drops. The classifier answers both shapes, so the
   * classifier is what decides.
   */
  const adoptedFromPrimary = useRef<string | null>(null);
  useEffect(() => {
    const text = primary.trim();
    if (text === "" || isEditing) return;
    if (adoptedFromPrimary.current === text) return;
    if (location !== null) return;
    if (planForLocationInput(text).action === "search") return;
    adoptedFromPrimary.current = text;
    void applyLocationPlan(text);
    // `applyLocationPlan` is deliberately absent: it closes over the same state this effect reads, and
    // depending on it would re-run the adoption whenever any of that changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, isEditing, location]);

  /*
   * The nearest saved area, inferred from the coordinate and nothing else.
   *
   * An effect, not a computation, because it writes state and must happen once per coordinate rather
   * than on every render — the ref is what makes "once" true, keyed on the coordinate so moving the pin
   * is a new derivation while a keystroke elsewhere is not. It never invents an area: `nearestSavedAreas`
   * returns a saved place's own areas or `null`, and `null` leaves the field empty rather than filled
   * with a plausible guess. A choice the user made is never overwritten; a value this effect derived is
   * re-derived when the pin moves.
   */
  const derivedAreaFor = useRef<string | null>(null);
  useEffect(() => {
    if (isEditing || location === null) return;
    const key = `${location.lat},${location.lon}`;
    if (derivedAreaFor.current === key) return;
    const items = savedPlaces.data?.items ?? [];
    // Wait for the list rather than locking in "nothing found" before it arrives.
    if (items.length === 0) return;
    derivedAreaFor.current = key;
    // Keep a choice the user made; only replace an area this effect itself filled in.
    if (selectedAreas.length > 0 && !derivedFields.has("area")) return;
    const nearest = nearestSavedAreas(items, location);
    if (nearest === null) return;
    setSelectedAreas(nearest);
    markDerivedField("area");
    /*
     * `markDerivedField` is a helper over `setDerivedFields`, which is listed here through
     * `derivedFields`: the mark change re-runs this effect, and the ref check at the top returns
     * immediately because the coordinate is now the one it last derived for. That is what keeps the
     * round trip from becoming a loop.
     */
  }, [location, isEditing, selectedAreas.length, savedPlaces.data, derivedFields]);

  const areaInputRef = useRef<HTMLInputElement>(null);

  /** Areas already chosen, folded, so a suggestion the user has used is not offered twice. */
  const chosenAreaKeys = useMemo(
    () => new Set(selectedAreas.map((area) => foldForCompare(area.name))),
    [selectedAreas],
  );

  function addArea(area: { id: string; name: string }): void {
    const key = foldForCompare(area.name);
    if (chosenAreaKeys.has(key)) return;
    setSelectedAreas((current) => [...current, area]);
    // The user chose this, so it is not the derived set any more — and the next pin move must not
    // silently replace it.
    clearDerivedField("area");
    setAreaDraft("");
  }

  /** Create the area on the server, which owns the folded-name idempotency. */
  async function commitAreaDraft(): Promise<void> {
    const trimmed = areaDraft.trim();
    if (trimmed === "") return;
    const existing = areas.data?.areas.find((area) => foldForCompare(area.name) === foldForCompare(trimmed));
    if (existing !== undefined) {
      addArea(existing);
      return;
    }
    try {
      const created = await createArea.mutateAsync(trimmed);
      addArea(created.area);
    } catch (error) {
      setFormError(error);
    }
  }

  /**
   * Adopt a coordinate the user handed over, and say where it came from.
   *
   * `source` decides which of the two sentences is shown, and they are genuinely different: a coordinate
   * parsed out of a link was already in the user's hands, while one the server had to fetch took a round
   * trip. Telling them "found" for the second when it cost a request, or "resolved" for the first when it
   * cost nothing, would both be wrong in the same small way — a message that does not match what
   * happened.
   */
  function adoptCoordinate(
    location: { lat: number; lon: number },
    /*
     * `resolvedName` and not `name`: the shadowing is not cosmetic.
     *
     * The first version took a `name` parameter, which shadowed the `name` *state* this function has to
     * read to decide whether the user has already written one. So `name.trim() === ""` compared the
     * parameter against itself, the branch never ran, and the place's name from the URL was silently
     * dropped — measured in the browser: a pasted share link adopted its coordinate and left the name
     * field empty. A parameter that hides the state it is being checked against is a bug with no error
     * message.
     */
    resolvedName: string | null,
    source: "link" | "resolved",
    resolvedUrl: string | null = null,
  ): void {
    /*
     * Every update below re-reads live state instead of the values captured when this was called.
     *
     * That is the whole reason for the functional form, and it is a measured defect, not a precaution. The
     * `resolved` path reaches here after an `await` — against the live endpoint, **1 330 ms**, and longer
     * on a phone. During that window the user is looking at a form they can type in: measured, a name
     * typed into the name field, or a pin placed on the map, was silently replaced by the link's own name
     * and coordinate, because `setLocation(location)` and the `name`-is-empty test both came from the
     * render that started the request. Every fact this function decides on is one the user can change
     * while it waits, so every one of them is read at the moment of writing.
     */
    const before = formSnapshotRef.current;
    /*
     * The mark is set from what the writes below will actually do, not from what the form asked for: a
     * resolved coordinate never overrides a pin the user placed during the round trip, so marking it
     * derived in that case would put the badge on a value they typed.
     */
    if (source === "link" || before.location === null) markDerivedField("location");

    setLocation((current) => (current !== null && source === "resolved" ? current : location));
    // The viewport moves to what was adopted, so the user sees the pin they just handed over rather than
    // a default view with a marker somewhere off screen. Focus follows the same rule as the pin: moving
    // the map is harmless, but not overriding a deliberate placement.
    setFocus((current) => (current !== null && source === "resolved" ? current : location));

    /*
     * The name fills a field that is **empty**, and never overwrites one that is not.
     *
     * One rule, both fields, and it needs no knowledge of which field holds the link — which matters,
     * because that knowledge is itself stale by the time this runs. `setPrimary(name)` used to run
     * unconditionally on adoption, and measured, pasting the share link on Home and pressing Lưu produced
     * a form whose leading field held `Phong Miên quán - forest slow café` with the pasted URL gone: the
     * place then saved with **no provenance at all**, discarding the one link the user had handed over —
     * the opposite of what ADR-004 asks for.
     *
     * "Empty means fill" satisfies every case without a branch on the URL: a leading field holding the
     * pasted link is not empty, so it survives; a name the user typed is not empty, so it survives; a
     * field they left blank gets the name the link carried, which is the starting point they wanted. It is
     * also the only version that stays correct when the user edits either field during the round trip.
     */
    if (resolvedName !== null && resolvedName !== "") {
      setPrimary((current) => (current.trim() === "" ? resolvedName : current));
      setName((current) => (current.trim() === "" ? resolvedName : current));
      /*
       * And it is marked derived only when it actually becomes the saved name: an empty leading field
       * takes it, or a leading field holding the link leaves the name field as the name. A typed name in
       * the leading field is the user's own, and marking it derived would put the badge on their words.
       */
      const nameFilled =
        before.primary.trim() === "" || (asUrl(before.primary) !== null && before.name.trim() === "");
      if (nameFilled) markDerivedField("name");
    }

    /*
     * The long URL, when the server resolved one.
     *
     * Google's short links are redirects and can stop resolving; the expanded URL is the durable address
     * of the place. Keeping it means the saved source is still useful in six months, which is the whole
     * point of recording provenance.
     */
    if (resolvedUrl !== null) setResolvedSourceUrl(resolvedUrl);

    setCandidates(null);
    setLookupStatus(null);
    setFormError(null);
    showToast(source === "resolved" ? copy.mapLink.resolved : copy.mapLink.fromLink);
  }

  /**
   * Do what the text asks for, whichever of the three things it is.
   *
   * Shared by the find field and the leading field, because the leading field is where a pasted Google
   * Maps link actually lands: Home's hero sends it to `/capture?q=…`, and a user who pastes a Maps link
   * there means "here is the place, here is where it is" — not "here is a link, now type the name again".
   * Two callers reading the same URL as two different kinds of thing is how one of them ends up not
   * adopting a coordinate the other would.
   *
   * Returns whether the text was handled as a location. `false` means it is a name, and the caller asks
   * the geocoder — which is the *only* remaining case.
   */
  async function applyLocationPlan(text: string, quiet = false): Promise<boolean> {
    const plan = planForLocationInput(text);

    if (plan.action === "adopt") {
      adoptCoordinate(plan.location, plan.name, "link");
      return true;
    }
    if (plan.action === "no-coordinate") {
      // A Maps link that carries no coordinate — a search for a name. Saying so is better than "not
      // found", because the user can go back and copy a link to the *place* instead.
      setCandidates(null);
      setLookupStatus(null);
      setFormError(null);
      showToast(copy.mapLink.noCoordinate);
      return true;
    }
    if (plan.action === "resolve") {
      setLooking(true);
      setFormError(null);
      setCandidates(null);
      setLookupStatus(null);
      try {
        const resolved = await manager.client.resolveMapLink({ url: plan.url });
        if (resolved.status === "ok" && resolved.location !== null) {
          adoptCoordinate(resolved.location, resolved.name, "resolved", resolved.resolved_url);
        } else if (!quiet) {
          /*
           * Every non-ok status has a sentence, written out as a switch rather than a nested ternary.
           *
           * The nested form had an `else` that absorbed whatever was left, which is how a status added
           * later inherits the wrong message without anybody choosing it. `not_a_map_link` cannot reach
           * here — the client only calls this endpoint when its own classifier said the input *is* a short
           * link, and the server uses the same classifier — but if it ever did, "copy a link to the place"
           * is the right advice, because in both cases there is nothing in the paste to adopt.
           */
          const message = ((): string => {
            switch (resolved.status) {
              case "unavailable":
                return copy.mapLink.unavailable;
              case "link_not_found":
                return copy.mapLink.notFound;
              case "no_coordinate":
              case "not_a_map_link":
                return copy.mapLink.noCoordinate;
              // `ok` with no location is the one combination the contract allows but the client cannot
              // act on: it means a coordinate was promised and none arrived. The generic failure is the
              // honest thing to show, because it is a server bug rather than anything about the link.
              case "ok":
                return copy.problem["invalid-response"];
            }
          })();
          showToast(message);
        }
      } catch (error) {
        setFormError(error);
      } finally {
        setLooking(false);
      }
      return true;
    }
    return false;
  }

  /**
   * Ask the provider now.
   *
   * The button does not call the provider itself: it pins the text the user wants searched, and the
   * query above answers — after the debounce the field would otherwise still be waiting on, and through
   * the same cache entry a typed suggestion already uses. Two request paths writing one list is how the
   * list ends up showing the answer to a question the user has already changed.
   */
  async function runLookup(): Promise<void> {
    const text = find.trim() === "" ? effectiveName : find.trim();
    if (text === "") return;
    // A coordinate, a full Maps URL or a share link is done here and never reaches the geocoder.
    if (await applyLocationPlan(text)) return;

    setFormError(null);
    setCandidates(null);
    setAiCandidates(null);
    setLookupStatus(null);
    lastAiQueryRef.current = "";
    setSubmittedFind(text);
  }

  async function runAiLookup(queryText?: string): Promise<void> {
    const q = (queryText ?? (find.trim() || effectiveName)).trim();
    if (!q) return;

    setAiCandidates(null);
    setLookupStatus(null);
    setFormError(null);
    try {
      const apiKey = getStoredGeminiKey();
      const res = await aiLookup.mutateAsync({
        query: q,
        ...(apiKey ? { api_key: apiKey } : {}),
      });

      if (res.status === "ok" && res.results.length > 0) {
        setAiCandidates(res.results);
      } else if (res.status === "quota_exceeded") {
        setLookupStatus("quota_exceeded");
        showToast(res.error_message || copy.capture.providerQuota);
      } else if (res.error_message) {
        showToast(res.error_message);
      } else {
        showToast(copy.capture.providerNoResults);
      }
    } catch (error) {
      setFormError(error);
    }
  }

  function adoptAiCandidate(candidate: AiPlaceLookupResult): void {
    /*
     * The name fills a field that is **empty**, and never overwrites one that is not.
     *
     * The same rule, and for the same measured reason, as `adoptCoordinate`: a pasted source link lives
     * in the leading field, and `setPrimary(candidate.name)` used to replace it unconditionally. Measured,
     * adopting an AI candidate while the leading field held the user's link saved the place under the
     * AI's name with **no `sources` row** — the one link the user handed over, dropped on the way in.
     * "Empty means fill" satisfies every case: a pasted link is not empty and survives, a typed name
     * survives, and a blank field gets a starting point.
     */
    if (candidate.name) {
      const nameFilled = primary.trim() === "" || (asUrl(primary) !== null && name.trim() === "");
      setPrimary((current) => (current.trim() === "" ? candidate.name : current));
      setName((current) => (current.trim() === "" ? candidate.name : current));
      if (nameFilled) markDerivedField("name");
    }
    if (candidate.address) {
      setAddress(candidate.address);
      setIsEditingAddress(false);
      markDerivedField("address");
    }
    /*
     * `location` is non-null only when the provider verified it from a Google Maps link, so this is the
     * one branch here that adopts a coordinate — and it adopts a link-backed one, never a model estimate.
     */
    if (candidate.location) {
      setLocation(candidate.location);
      setFocus(candidate.location);
      markDerivedField("location");
    }
    if (candidate.categories && candidate.categories.length > 0) {
      setCategories(candidate.categories);
      markDerivedField("categories");
    }
    if (candidate.best_time && candidate.best_time.length > 0) {
      setBestTime(candidate.best_time);
    }
    if (candidate.notes && note.trim() === "") {
      setNote(candidate.notes);
    }
    if (candidate.google_maps_url) {
      setResolvedSourceUrl(candidate.google_maps_url);
    }
    setAiCandidates(null);
    setCandidates(null);
    setLookupStatus(null);
    showToast(copy.capture.aiApply);
  }

  useEffect(() => {
    if (params.get("ai") !== "1" || aiAutoSearched.current) return;
    const q = params.get("q")?.trim() || primary.trim() || find.trim();
    if (!q) return;
    // A pasted link is not a question. The capture form resolves it itself, and sending it to Gemini
    // would ask the model about a URL rather than a place — the same guard the suggestion row and the
    // search effect carry.
    if (asUrl(q) !== null) return;
    aiAutoSearched.current = true;
    void runAiLookup(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, primary, find]);

  /**
   * Where the map pin is offered after a search found nothing.
   *
   * Reveals the map and, when the user has not placed a pin or a viewport, looks at the search centre —
   * which places nothing. A coordinate is stored only when the user supplies one, so the gesture is
   * "here is the map", not a guess at a position dressed up as an answer.
   */
  function showMapPin(): void {
    if (location === null && focus === null) setFocus(centre);
    mapCardRef.current?.scrollIntoView({ block: "start" });
  }

  function adopt(candidate: LookupCandidate): void {
    setLocation(candidate.location);
    markDerivedField("location");
    // The viewport moves to the adopted coordinate without the pin being moved again — and without
    // discarding a pin the user placed deliberately.
    setFocus(candidate.location);
    // Provenance: the index that answered and its identity for the place, so the save records where the
    // coordinate came from rather than presenting it as something the user typed.
    setAdoptedProvider({ provider: candidate.provider, providerPlaceId: candidate.providerPlaceId });
    if (candidate.address !== null && address.trim() === "") {
      setAddress(candidate.address);
      setIsEditingAddress(false);
      markDerivedField("address");
    }
    if (effectiveName === "" && candidate.name !== "") {
      setName(candidate.name);
      setPrimary((current) => (current.trim() === "" ? candidate.name : current));
      markDerivedField("name");
    }
    /*
     * The provider's categories, when it gave any and the user has not chosen. This is the provider
     * answering "what kind of place is this" — the question the chips would otherwise make them answer
     * again — and a user's own selection is never replaced.
     */
    if (candidate.categories.length > 0 && categories.length === 0) {
      setCategories(candidate.categories);
      markDerivedField("categories");
    }
    setCandidates(null);
    setLookupStatus(null);
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    // Before the early return below, so a double tap with an empty name is also one attempt.
    if (submitting.current) return;
    submitting.current = true;
    try {
      await saveOnce();
    } finally {
      submitting.current = false;
    }
  }

  async function saveOnce(): Promise<void> {
    setFormError(null);

    if (effectiveName === "") {
      // The only required field, and the message says what to do rather than which field it is: the
      // user may have pasted a link and not noticed the name field below it.
      setFormError(new Error("required-name"));
      return;
    }

    try {
      if (isEditing) {
        /*
         * Edit sends the place's own fields, which is exactly what `PATCH /places/:id` takes, plus the
         * source the user attached above.
         *
         * `sources` is additive on the update schema: this list holds only the link just pasted (the
         * form does not load the place's stored provenance), so a PATCH that omits it leaves the
         * existing rows alone, and a PATCH that carries it appends. An edit that touches only a phone
         * number therefore cannot delete where the place came from.
         */
        await updatePlace.mutateAsync({
          name: effectiveName,
          categories,
          subcategory: subcategory.trim() === "" ? null : subcategory.trim(),
          address: address.trim() === "" ? null : address.trim(),
          location,
          best_time: bestTime,
          description: note.trim() === "" ? null : note.trim(),
          // The suitability answers, which had no way in from any screen until now.
          ...suitability,
          area_ids: selectedAreas.map((area) => area.id),
          ...(sourceInputs.length > 0 ? { sources: sourceInputs } : {}),
        });
        /*
         * An edit lands on the place's own card, and says the change was kept.
         *
         * `savedEdit` rather than `saved(name)`: the same two strings this file already had, and the
         * distinction is the point — an edit saved no new place, and a sentence naming the place would
         * read as though one had been created. The card is where the edited fields are shown, and it is
         * where Cancel already sends an abandoned edit, so keeping and abandoning agree about where this
         * form "is".
         */
        showToast(copy.capture.savedEdit);
        void navigate(originPath, { replace: true });
        return;
      }

      /*
       * The save-time duplicate step (AC-3.5), before anything is written.
       *
       * Skipped once the user has said "these are two places" (`duplicateAcknowledged`), which is the
       * dismissal that makes the warning advice rather than a block — measured, the rule flags two
       * branches of one chain 149 m apart, and the user is the only one who can tell them apart. The
       * rule needs both a name and a coordinate: proximity is half of it, and a name-only match would be
       * a guess presented as a fact. So a draft with no coordinate is not checked, and the screen says
       * exactly that rather than returning in silence — not-known is not a match.
       */
      if (!duplicateAcknowledged.current) {
        if (location === null) {
          setDuplicateNotice({ kind: "unchecked" });
          return;
        }
        const check = await duplicateCheck.mutateAsync({
          name: effectiveName,
          lat: location.lat,
          lon: location.lon,
        });
        if (check.duplicates.length > 0) {
          setDuplicateNotice({ kind: "matches", candidates: check.duplicates });
          return;
        }
      }

      const created = await createPlace.mutateAsync({
        // One key for this form's save, so both submissions of a double tap are one place.
        idempotencyKey: saveKey.current,
        body: buildCreateBody(),
      });
      /*
       * A new place lands on the list, named in the toast.
       *
       * The toast comes from the `ToastProvider` in `App`, which sits *outside* `Shell` and therefore
       * outside every route, so the message outlives this screen being unmounted by the navigation below.
       * Shown first only for readability; either order survives, because the provider is never the
       * component being replaced.
       *
       * `replace` and not `push`: the form is finished, and leaving it in history means Back from the list
       * re-opens a blank capture form carrying the same prefilled text — and a save there would be a second
       * place, since the fresh mount has a fresh idempotency key. The completed form is not somewhere to
       * return to.
       */
      showToast(copy.capture.saved(created.place.name));
      void navigate(originPath, { replace: true });
    } catch (error) {
      setFormError(error);
    }
  }

  /**
   * The body a create sends.
   *
   * A function rather than an inline literal, because two paths now send it: an ordinary save, and the
   * merge that folds this draft into the place it duplicates. Both must write the same fields — a
   * paste-adopted link, a note, a provider source — or the merge path would quietly lose the very
   * provenance that made the place worth capturing. The field-by-field build (rather than a spread of
   * conditionals) is the same choice `createPlace`'s handler makes, for the same reason: an unknown key
   * is dropped without a type error, so the shape is written out where the compiler can check it.
   */
  function buildCreateBody(): CreatePlaceBody {
    return {
      name: effectiveName,
      ...(categories.length > 0 ? { categories } : {}),
      ...(subcategory.trim() !== "" ? { subcategory: subcategory.trim() } : {}),
      ...(address.trim() !== "" ? { address: address.trim() } : {}),
      ...(location !== null ? { location } : {}),
      ...(bestTime.length > 0 ? { best_time: bestTime } : {}),
      /*
       * `description`, not `note`.
       *
       * The textarea was sendable as either, and the two went to different tables: `description` is
       * the place's own field, and `note` belonged to a membership row this form no longer writes.
       * Edit writes `description`, so typing it on create and seeing it appear after a later edit was
       * the honest-looking half of a split. One field, one destination.
       */
      ...(note.trim() !== "" ? { description: note.trim() } : {}),
      // Sent when answered, omitted when not: `null` on the wire means "clear", and a create has
      // nothing to clear — an unanswered attribute is simply absent.
      ...Object.fromEntries(Object.entries(suitability).filter(([, value]) => value !== null)),
      ...(selectedAreas.length > 0 ? { area_ids: selectedAreas.map((area) => area.id) } : {}),
      /*
       * Provenance: the link if there was one, and the provider if the place came from the map.
       *
       * Both, in one list, because they are different facts and a place can have both — a user who
       * pasted a post about a café and then took the coordinate from the map has two sources, and the
       * `sources` column exists precisely so neither is invented at the other's expense (AC-5.2).
       *
       * The provider entry is why adopting a map result is not a manual capture: `place_sources` with
       * `kind = 'provider_search'` is what lets the same place be recognised again by identity rather
       * than by name, which is the dedup path `find_place_by_provider` reads.
       */
      ...(sourceInputs.length > 0 ? { sources: sourceInputs } : {}),
    };
  }

  /**
   * The dismissal: continue the save.
   *
   * Called from either state of the notice — a duplicate to step past, or a check that could not run for
   * want of a coordinate — so it is the user's answer to the step rather than a second ask. It sets
   * `duplicateAcknowledged` before re-entering the save, which is what stops the step from stopping again
   * on the way through.
   */
  function saveAnyway(): void {
    if (submitting.current) return;
    duplicateAcknowledged.current = true;
    setDuplicateNotice(null);
    submitting.current = true;
    void saveOnce().finally(() => {
      submitting.current = false;
    });
  }

  /**
   * Fold this draft into a place it duplicates.
   *
   * The draft is created first so it has an identity, then merged — in one transaction, through the
   * repository's own `mergePlaces`. That is what carries the pasted link, the note and the provenance
   * onto the survivor and soft-deletes the draft, leaving one place rather than two. Creating it first
   * is not a second place surviving: the merge runs immediately and the draft never appears in a list.
   *
   * If the fold fails, the draft is real — so the user is told and taken to it, rather than left on a
   * form whose next save would make a third.
   */
  async function mergeIntoDuplicate(candidateId: string): Promise<void> {
    if (submitting.current) return;
    submitting.current = true;
    setFormError(null);
    try {
      const created = await createPlace.mutateAsync({
        idempotencyKey: saveKey.current,
        body: buildCreateBody(),
      });
      try {
        const merged = await mergePlaces.mutateAsync({ keepId: candidateId, duplicateId: created.place.id });
        showToast(copy.capture.mergedInto(merged.place.name));
        void navigate(`/places/${merged.place.id}`, { replace: true });
      } catch {
        showToast(copy.capture.mergeFailed);
        void navigate(`/places/${created.place.id}`, { replace: true });
      }
    } catch (error) {
      setFormError(error);
    } finally {
      submitting.current = false;
    }
  }

  const fieldErrors = fieldMessagesFor(formError);
  const requiredNameFailed = formError instanceof Error && formError.message === "required-name";
  /*
   * Whether the provider offered anything worth taking.
   *
   * `matchConfidence` is the same rule the rows use to decide whether to print an accept button, so this
   * cannot disagree with what is on screen: a list where every row is "unusable" is a list with nothing
   * to adopt, and the pin is then the only way forward.
   */
  const hasUsableCandidate = (candidates ?? []).some((candidate) => matchConfidence(candidate.matchQuality) !== "unusable");

  /*
   * The sources this save records, in one list.
   *
   * `SourceInput` has no id: the server assigns it. The provider entry is only added when the provider
   * id arrived with the candidate, because a `provider_search` source with no identity behind it would
   * claim the place was resolved by a provider without recording which one or which place — which is
   * exactly the field the dedup path reads.
   *
   * Two ways a candidate arrives and both are covered: the quick-capture bar hands one over in the query
   * string (`prefill`), and the find field on this form adopts one in place (`adoptedProvider`). Before
   * this, the second path saved the coordinate with no provenance at all.
   */
  const providerProvenance =
    prefill.provider !== null && prefill.providerPlaceId !== null
      ? { provider: prefill.provider, providerPlaceId: prefill.providerPlaceId }
      : adoptedProvider;

  const sourceInputs = [
    ...(providerProvenance !== null && providerProvenance.providerPlaceId !== null
      ? [
          {
            kind: "provider_search" as const,
            provider: providerProvenance.provider,
            provider_place_id: providerProvenance.providerPlaceId,
            attribution: null,
            url: null,
          },
        ]
      : []),
    ...(primaryAsUrl !== null
      ? [
          {
            kind: "url" as const,
            // The expanded URL when there is one: a short link can expire into a redirect to nothing,
            // and this row is what lets the user find the place again.
            url: resolvedSourceUrl ?? primaryAsUrl,
            attribution: copy.capture.sourceAttribution,
            provider: null,
            provider_place_id: null,
          },
        ]
      : []),
  ];

  /*
   * The form used to be replaced here by a "saved" panel with two buttons, once `savedName` was set.
   *
   * Nothing routes to it now: both save paths above show a toast and navigate away — the list for a new
   * place, its own card for an edit — so the panel was unreachable and is gone rather than left as dead
   * markup. Its four class names went with it, out of `screens.css`.
   */

  return (
    <form className="screen" onSubmit={(event) => void onSubmit(event)} autoComplete="off">
      <div className="screen__head">
        <div>
          <h1 className="screen__heading">{isEditing ? copy.capture.editHeading : copy.capture.heading}</h1>
          <p className="screen__sub">
            {isEditing
              ? copy.capture.subtitleEdit
              : copy.capture.subtitleCreate}
          </p>
        </div>
        <div className="screen__head-actions">
          <button
            type="button"
            className="button"
            onClick={goBack}
          >
            {copy.capture.cancel}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={createPlace.isPending || updatePlace.isPending}
          >
            {createPlace.isPending || updatePlace.isPending ? copy.capture.saving : copy.capture.save}
          </button>
        </div>
      </div>

      {/*
        Why the form is already filled in.

        Without this the prefilled field is a small mystery — the user pasted a link two screens ago and
        the field looks typed-in, or looks empty because they did not scroll. Saying where it came from is
        what stops the second paste, which is the defect this prefill exists to fix.
      */}
      {!isEditing && (fromMap || prefill.text !== "") && (
        <p className="section__note">
          {fromMap ? copy.capture.prefilledFromMap : copy.capture.prefilledFromText}
        </p>
      )}

      <div className="capture__split">
        {/* Left Column (~55%): Core details */}
        <div className="capture__col-main">
          {/* Source badge if link detected */}
          {primaryAsUrl !== null && sourceSummary !== null && (
            <div className="capture__source-badge">
              <div className="capture__source-info">
                <span className="capture__source-icon" aria-hidden="true">🔗</span>
                <span className="capture__source-platform">{sourceSummary.platform}:</span>
                <span className="capture__source-text" title={primaryAsUrl}>{sourceSummary.label}</span>
              </div>
              <button
                type="button"
                className="button button--ghost capture__source-change"
                onClick={() => {
                  setPrimary("");
                  setName("");
                  setResolvedSourceUrl(null);
                }}
              >
                {copy.capture.changeLink}
              </button>
            </div>
          )}

          {/* Hero Place Name Input */}
          <label className="field capture__hero-field">
            <span className="field__label capture__hero-label">
              {copy.capture.nameLabel} <em className="capture__hero-req">· {copy.capture.nameRequired}</em>
              {derivedFields.has("name") && <DerivedMark />}
            </span>
            {primaryAsUrl !== null ? (
              <input
                className="field__input field__input--lead field__input--hero"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  // Typing makes the value the user's own, so the mark that said otherwise goes.
                  clearDerivedField("name");
                }}
                placeholder={nameFromUrl(primaryAsUrl) || copy.capture.namePlaceholder}
                autoFocus
              />
            ) : (
              <input
                className="field__input field__input--lead field__input--hero"
                value={primary}
                onChange={(event) => {
                  const val = event.target.value;
                  setPrimary(val);
                  clearDerivedField("name");
                  const parsed = asUrl(val);
                  if (parsed !== null) {
                    const guess = nameFromUrl(parsed);
                    if (guess && name.trim() === "") setName(guess);
                  }
                }}
                placeholder={copy.capture.primaryPlaceholder}
                autoFocus
              />
            )}
            <span className="field__help">
              {primaryAsUrl === null
                ? (isEditing ? copy.capture.primaryHelpEdit : copy.capture.primaryHelp)
                : copy.capture.primaryHelpLink}
            </span>
          </label>

          {/* Own-place suggestions, while typing a name */}
          {suggestions.isSuccess && suggestions.data.items.length > 0 && (
            <ul className="suggestions" role="listbox">
              {suggestions.data.items.map((place) => (
                <li key={place.id}>
                  <button
                    type="button"
                    className="suggestion"
                    onClick={() => {
                      setPrimary(place.name);
                      setName(place.name);
                      if (place.address !== null) setAddress(place.address);
                      if (place.location !== null) {
                        setLocation(place.location);
                        setFocus(place.location);
                      }
                    }}
                  >
                    <span>{place.name}</span>
                    {place.location === null && <span className="place__flag">{copy.places.noCoordinates}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Categories */}
          <div className="field">
            <span className="field__label">
              {copy.capture.categoryLabel} <em>· {copy.capture.categoryMulti}</em>
              {derivedFields.has("categories") && <DerivedMark />}
            </span>
            <div className="chips">
              {categoryChoices.map((choice) => {
                const selected = categories.includes(choice.value);
                return (
                  <button
                    key={choice.value}
                    type="button"
                    className={selected ? "chip chip--on" : "chip"}
                    aria-pressed={selected}
                    onClick={() => {
                      clearDerivedField("categories");
                      setCategories((current) =>
                        selected ? current.filter((value) => value !== choice.value) : [...current, choice.value],
                      );
                    }}
                  >
                    {choice.glyph} {choice.label}
                  </button>
                );
              })}
            </div>

            {categories.includes("other") && (
              <label className="field subfield">
                <span className="field__label">
                  {copy.capture.subcategoryLabel} <em>· {copy.capture.subcategoryRequired}</em>
                </span>
                <input
                  className="field__input"
                  value={subcategory}
                  onChange={(event) => setSubcategory(event.target.value)}
                  placeholder={copy.capture.subcategoryPlaceholder}
                />
              </label>
            )}
          </div>

          {/* Areas */}
          <div className="field">
            <span className="field__label">
              {copy.capture.areaLabel}
              {derivedFields.has("area") && <DerivedMark />}
            </span>
            <div
              className="tags"
              onClick={() => areaInputRef.current?.focus()}
            >
              {selectedAreas.map((area) => (
                <span key={area.id} className="tag">
                  {area.name}
                  <button
                    type="button"
                    className="tag__remove"
                    aria-label={copy.capture.areaRemove(area.name)}
                    onClick={(event) => {
                      event.stopPropagation();
                      clearDerivedField("area");
                      setSelectedAreas((current) => current.filter((candidate) => candidate.id !== area.id));
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                ref={areaInputRef}
                className="tags__input"
                value={areaDraft}
                onChange={(event) => setAreaDraft(event.target.value)}
                placeholder={selectedAreas.length === 0 ? copy.capture.areaPlaceholder : ""}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && areaDraft === "" && selectedAreas.length > 0) {
                    setSelectedAreas((current) => current.slice(0, -1));
                    return;
                  }
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  void commitAreaDraft();
                }}
              />
            </div>
            {areaDraft.trim() !== "" && (
              <ul className="suggestions" role="listbox">
                {(areas.data?.areas ?? [])
                  .filter(
                    (area) =>
                      !chosenAreaKeys.has(foldForCompare(area.name)) &&
                      foldForCompare(area.name).includes(foldForCompare(areaDraft)),
                  )
                  .slice(0, 6)
                  .map((area) => (
                    <li key={area.id}>
                      <button
                        type="button"
                        className="suggestion"
                        onClick={() => {
                          addArea(area);
                          areaInputRef.current?.focus();
                        }}
                      >
                        <span>{area.name}</span>
                      </button>
                    </li>
                  ))}
                {!(areas.data?.areas ?? []).some(
                  (area) => foldForCompare(area.name) === foldForCompare(areaDraft.trim()),
                ) && (
                  <li>
                    <button
                      type="button"
                      className="suggestion"
                      onClick={() => {
                        void commitAreaDraft();
                        areaInputRef.current?.focus();
                      }}
                    >
                      <span>+ {areaDraft.trim()}</span>
                      <span className="place__flag">{copy.capture.areaCreate}</span>
                    </button>
                  </li>
                )}
              </ul>
            )}
            <span className="field__help">{copy.capture.areaHelp}</span>
          </div>

          {/* Secondary Details Accordion */}
          <button
            type="button"
            className="disclosure"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            <span className="disclosure__arrow" aria-hidden="true">
              {detailsOpen ? "▴" : "▾"}
            </span>{" "}
            {detailsOpen ? copy.capture.detailsCollapse : copy.capture.detailsExpand}{" "}
            <span className="disclosure__hint">{copy.capture.detailsSecondaryHint}</span>
          </button>

          {detailsOpen && (
            <div className="details">
              <fieldset className="field field--group">
                <legend className="field__label">{copy.capture.bestTimeLabel}</legend>
                <div className="checks">
                  {BEST_TIME_CHOICES.map((choice) => (
                    <label key={choice.value} className="check">
                      <input
                        type="checkbox"
                        checked={bestTime.includes(choice.value)}
                        onChange={() => setBestTime((current) => toggleBestTime(current, choice.value) as BestTimeSlot[])}
                      />
                      <span>{choice.label}</span>
                    </label>
                  ))}
                </div>
                <span className="field__help">{copy.capture.bestTimeHelp}</span>
              </fieldset>

              <fieldset className="field field--group">
                <legend className="field__label">{copy.capture.suitabilityLabel}</legend>
                <div className="suitability">
                  {SUITABILITY_CHOICES.map((choice) => {
                    const value = suitability[choice.value];
                    return (
                      <div key={choice.value} className="suitability__row">
                        <span className="suitability__label">{choice.label}</span>
                        <div className="suitability__segmented" role="group" aria-label={choice.label}>
                          <button
                            type="button"
                            className={
                              value === true
                                ? "suitability__seg-btn suitability__seg-btn--yes suitability__seg-btn--active"
                                : "suitability__seg-btn suitability__seg-btn--yes"
                            }
                            aria-pressed={value === true}
                            onClick={() => setSuitabilityFlag(choice.value, true)}
                          >
                            {copy.capture.suitabilityYes}
                          </button>
                          <button
                            type="button"
                            className={
                              value === false
                                ? "suitability__seg-btn suitability__seg-btn--no suitability__seg-btn--active"
                                : "suitability__seg-btn suitability__seg-btn--no"
                            }
                            aria-pressed={value === false}
                            onClick={() => setSuitabilityFlag(choice.value, false)}
                          >
                            {copy.capture.suitabilityNo}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <span className="field__help">{copy.capture.suitabilityHelp}</span>
              </fieldset>

              <label className="field">
                <span className="field__label">{copy.capture.noteLabel}</span>
                <textarea
                  className="field__input field__input--area"
                  rows={2}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        {/* Right Column (~45%): Interactive Map & Geocoding */}
        <div className="capture__col-aside">
          <div className="capture__map-card" ref={mapCardRef}>
            <div className="capture__map-card-header">
              <h2 className="capture__map-card-title">
                <span aria-hidden="true">📍</span> {copy.capture.mapHeading}
                {derivedFields.has("location") && <DerivedMark />}
              </h2>
              {location !== null ? (
                <span className="chip chip--sm chip--on">✓ {copy.capture.hasCoordinates}</span>
              ) : (
                <span className="chip chip--sm place__flag">{copy.places.noCoordinates}</span>
              )}
            </div>

            {/* Finding a place by provider / address search */}
            <div className="field">
              <span className="field__label">{copy.capture.findLabel}</span>
              <div className="capture__search-box">
                <span className="capture__search-icon" aria-hidden="true">🔍</span>
                <input
                  className="capture__search-input"
                  value={find}
                  onChange={(event) => {
                    setFind(event.target.value);
                    // The user is typing again, so the pinned search is stale: the query follows the
                    // field from here, and the debounce is what paces it.
                    setSubmittedFind(null);
                  }}
                  placeholder={copy.capture.findPlaceholder}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void runLookup();
                    }
                  }}
                />
                <button
                  type="button"
                  className="button button--primary capture__search-btn"
                  disabled={searching || aiLookup.isPending}
                  onClick={() => void runLookup()}
                >
                  {searching
                    ? copy.capture.searching
                    : aiLookup.isPending
                      ? copy.capture.aiSearching
                      : copy.capture.findButton}
                </button>
              </div>
              <span className="field__help">{copy.capture.findHelp}</span>

              {/* While AI is searching automatically in background */}
              {aiLookup.isPending && (
                <div className="candidates">
                  <p className="field__help">{copy.capture.aiAutoSearching}</p>
                </div>
              )}

              {/* AI search candidates */}
              {aiCandidates !== null && aiCandidates.length > 0 && (
                <div className="candidates">
                  <span className="field__label">{copy.capture.aiHeading}</span>
                  <ul className="cards">
                    {aiCandidates.map((candidate, idx) => (
                      <li key={`ai-${idx}-${candidate.name}`} className="card card--row">
                        <div className="card__link">
                          <span className="card__title">
                            {candidate.name}
                            {/* The coordinate is only ever taken from a cited Google Maps link, so a
                                non-null `location` is a verified one and this badge is honest. */}
                            {candidate.location !== null && <span className="badge">{copy.capture.hasCoordinates}</span>}
                          </span>
                          {candidate.address !== null && <span className="card__meta">📍 {candidate.address}</span>}
                          {candidate.categories.length > 0 && (
                            <span className="card__meta">🏷️ {candidate.categories.join(", ")}</span>
                          )}
                          {candidate.best_time.length > 0 && (
                            <span className="card__meta">⏰ {candidate.best_time.join(", ")}</span>
                          )}
                          {candidate.notes !== null && (
                            <span className="card__meta">💡 {candidate.notes}</span>
                          )}
                        </div>
                        <button type="button" className="link" onClick={() => adoptAiCandidate(candidate)}>
                          {copy.capture.aiApply}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Map search candidates */}
              {candidates !== null && candidates.length > 0 && (
                <div className="candidates">
                  <span className="field__label">{copy.capture.providerSuggestHeading}</span>
                  <ul className="cards">
                    {candidates.map((candidate) => {
                      const confidence = matchConfidence(candidate.matchQuality);
                      return (
                        <li key={`${candidate.provider}-${candidate.providerPlaceId ?? candidate.name}`} className="card card--row">
                          <div className="card__link">
                            <span className="card__title">{candidate.name}</span>
                            {candidate.address !== null && <span className="card__meta">{candidate.address}</span>}
                            {/* Which index answered, in words — the two have different coverage. */}
                            <span className="card__meta">
                              {copy.capture.providerSourceLabel}{" "}
                              {copy.capture.providerLabels[candidate.provider] ?? copy.capture.providerLabelFallback}
                            </span>
                            {confidence !== "strong" && (
                              <span className="card__meta card__meta--warn">
                                {confidence === "weak" ? copy.capture.providerWeak : copy.capture.providerUnusable}
                              </span>
                            )}
                          </div>
                          {confidence !== "unusable" && (
                            <button type="button" className="link" onClick={() => adopt(candidate)}>
                              {copy.capture.providerUse}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {lookupStatus === "unavailable" && (
                <div className="candidates">
                  <p className="form__error">{copy.capture.providerUnavailable}</p>
                </div>
              )}
              {lookupStatus === "quota_exceeded" && <p className="form__error">{copy.capture.providerQuota}</p>}

              {/* When map has no results and AI finished with no results either */}
              {candidates !== null && !hasUsableCandidate && (!aiCandidates || aiCandidates.length === 0) && !aiLookup.isPending && !searching && (
                <div className="candidates">
                  <p className="field__help">{copy.capture.providerNoResults}</p>
                  <button type="button" className="button" onClick={showMapPin}>
                    {copy.capture.providerPlaceOnMap}
                  </button>
                </div>
              )}
            </div>

            {/* Address */}
            {address.trim() !== "" && !isEditingAddress ? (
              <div className="capture__address-card">
                <span className="capture__address-icon" aria-hidden="true">📍</span>
                <div className="capture__address-info">
                  <span className="capture__address-label">
                    {copy.capture.addressLabel}
                    {derivedFields.has("address") && <DerivedMark />}
                  </span>
                  <span className="capture__address-text">{address}</span>
                </div>
                <button
                  type="button"
                  className="capture__address-action"
                  onClick={() => setIsEditingAddress(true)}
                >
                  {copy.capture.addressEdit}
                </button>
              </div>
            ) : (
              <label className="field subfield">
                <div className="capture__address-bar">
                  <span className="field__label">
                    {copy.capture.addressLabel}
                    {derivedFields.has("address") && <DerivedMark />}
                  </span>
                  {address.trim() !== "" && isEditingAddress && (
                    <button
                      type="button"
                      className="capture__address-done"
                      onClick={() => setIsEditingAddress(false)}
                    >
                      {copy.capture.addressDone}
                    </button>
                  )}
                </div>
                <input
                  className="field__input capture__address-input"
                  value={address}
                  onChange={(event) => {
                    setAddress(event.target.value);
                    clearDerivedField("address");
                  }}
                  placeholder={copy.capture.addressPlaceholder}
                />
              </label>
            )}

            {/* Leaflet Map */}
            <div className="field">
              <Suspense fallback={<p className="field__help">{copy.state.loading}</p>}>
                <MapPicker
                  value={location}
                  onChange={(next) => {
                    setLocation(next);
                    // A pin the user placed or moved is theirs, so the "filled from a link" mark goes.
                    clearDerivedField("location");
                  }}
                  focus={focus}
                />
              </Suspense>
              <span className="field__help capture__map-hint">
                {location !== null
                  ? copy.capture.mapHintChange
                  : copy.capture.mapHintSet}
              </span>
            </div>
          </div>
        </div>
      </div>

      {requiredNameFailed && (
        <p className="form__error" role="alert">
          {copy.capture.requiredName}
        </p>
      )}
      {formError !== null && !requiredNameFailed && fieldErrors.size === 0 && (
        <p className="form__error" role="alert">
          {messageFor(formError)}
        </p>
      )}
      {[...fieldErrors.entries()].map(([field, messages]) => (
        <ul key={field} className="field__errors">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ))}

      {/*
        The save-time duplicate step (AC-3.5), in the three states the notice can be in.

        A notice, not a block, and every control below is a way out of it. `role="alert"` so a screen
        reader announces it when the save is attempted. Two shapes share the block: candidates the rule
        can see — merge into one, save as a new place anyway, or step back — and the coordinate-less
        draft the rule cannot run on, which says so rather than passing for a clean bill of health.
        Measured, the rule flags two branches of one chain 149 m apart — a false alarm it cannot avoid —
        so "Vẫn lưu" must be frictionless and the copy must say *may*, never *is*.
      */}
      {duplicateNotice !== null && (
        <div className="duplicates" role="alert">
          {duplicateNotice.kind === "matches" ? (
            <>
              <p className="duplicates__heading">{copy.capture.duplicateHeading}</p>
              <ul className="duplicates__list">
                {duplicateNotice.candidates.map((candidate) => (
                  <li key={candidate.id} className="duplicates__row">
                    <span className="duplicates__text">{copy.capture.duplicateDetail(candidate.name)}</span>
                    <button
                      type="button"
                      className="button button--primary duplicates__action"
                      disabled={createPlace.isPending}
                      onClick={() => void mergeIntoDuplicate(candidate.id)}
                    >
                      {copy.capture.duplicateMerge(candidate.name)}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="duplicates__heading">{copy.capture.duplicateUncheckedHeading}</p>
              <p className="duplicates__text">{copy.capture.duplicateUncheckedDetail}</p>
            </>
          )}
          <div className="duplicates__actions">
            <button
              type="button"
              className="button duplicates__action"
              disabled={createPlace.isPending}
              onClick={saveAnyway}
            >
              {duplicateNotice.kind === "matches"
                ? copy.capture.duplicateSaveAnyway
                : copy.capture.duplicateUncheckedSave}
            </button>
            <button
              type="button"
              className="button button--ghost duplicates__action"
              onClick={() => setDuplicateNotice(null)}
            >
              {copy.capture.duplicateDismiss}
            </button>
          </div>
        </div>
      )}

      {/* Sticky Savebar */}
      <div className="savebar">
        <div className="savebar__inner">
          <button
            type="button"
            className="button capture__cancel-btn"
            onClick={goBack}
          >
            {copy.capture.cancel}
          </button>
          <button
            type="submit"
            className="button button--primary capture__save-btn"
            disabled={createPlace.isPending || updatePlace.isPending}
          >
            {createPlace.isPending || updatePlace.isPending ? copy.capture.saving : copy.capture.save}
          </button>
        </div>
      </div>
    </form>
  );
}
